import { useContext, useEffect, useState, useEffectEvent } from 'react';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FormProvider } from 'react-hook-form';

import { OfferEventFormFields } from 'offer/forms/OfferEventForm/OfferEventFormFields';
import { MeetingFormFields } from 'offer/forms/OfferEventForm/MeetingFormFields';
import { OfferFormFields } from 'offer/forms/OfferEventForm/OfferFormFields';
import {
    EOfferEventCategoryType,
    TOfferEventFormValues,
    TOfferFormValues,
} from 'offer/types';
import {
    IContentTypeValue,
    IFeature,
    IOfferEventDetailFragment,
    IOfferEventType,
    IOfferEventsQuery,
    IRole,
    IUpdateOfferEventInput,
    OfferEventsDocument,
    CurrentUserDocument,
    OrganisationDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { DescriptionButton } from 'common/components/DescriptionButton';
import { DescriptionBox } from 'common/components/DescriptionBox';
import { getOfferEventTypeTranslation } from 'offer/utils/offerEvent';
import { Loader } from 'common/components/Loader';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { OfferPermissionsContext } from 'offer/contexts';
import { Link } from 'common/components/Link';
import { ShowAfterDelay } from 'common/components/ShowAfterDelay';
import { FormSection } from 'common/components/FormSection';
import { OfferTypeSelectField } from 'offer/forms/Field';
import { useFeature } from 'common/hooks/useFeature';
import { FormTrainerSelectorField } from 'user/forms/Field';
import {
    CertificateSelectField,
    SliderField,
    SwitchField,
    TextField,
} from 'common/forms/Field';
import {
    CourseSelectorField,
    ScormSelectorField,
    TrainingSelectorField,
} from 'training/forms/Field';
import { ExternalContentSelectorField } from 'external/forms/Field';
import { VideoSelectorField } from 'video/forms/Field';
import { GroupConditionsSection } from 'offer/forms/OfferEventForm/GroupConditionsSection';
import { FormFooter } from 'common/components/FormFooter';
import { SubscribeSettingsSection } from 'offer/forms/OfferEventForm/SubscribeSettingsSection';
import { MoreSettingsExpander } from 'offer/forms/OfferEventForm/MoreSettingsExpander';
import { useForm } from 'offer/forms/OfferEventForm/hooks/useForm';

interface IProps {
    disabled?: boolean;
    edit?: boolean;
    includeOfferFields?: boolean;
    initialValues?: TOfferEventFormValues;
    isOfferDetailDrawer?: boolean;
    offerEvent?: IOfferEventDetailFragment;
    offerEventType: IOfferEventType;
    offerEventCategoryType: EOfferEventCategoryType;
    onClose?(): void;
    onDelete?(): void;
    onLoading?(loading: boolean): void;
    onSubmit(values: TOfferFormValues | IUpdateOfferEventInput): void;
}

export const OfferEventForm = ({
    disabled,
    edit,
    includeOfferFields,
    isOfferDetailDrawer,
    offerEvent,
    offerEventType,
    offerEventCategoryType,
    onClose,
    onDelete,
    onLoading,
    onSubmit,
}: IProps) => {
    const { showApolloError } = useApolloError();
    const [translate] = useTranslation();
    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const { canUse: canUseScorm, loading: loadingCanUseScorm } = useFeature(
        IFeature.Scorm
    );
    const { canUse: canUseVideo, loading: loadingCanUseVideo } = useFeature(
        IFeature.Video
    );
    const {
        data: organisationData,
        loading: loadingOrg,
        error,
    } = useQuery(OrganisationDocument, {});

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const currentUser = currentUserData?.currentUser;
    const currentUserIsAuthor =
        currentUser?.roles?.includes(IRole.Author) &&
        !currentUser.roles.includes(IRole.Manager);

    const { organisation } = organisationData || {};
    const hasExternalContent = !!organisation?.hasExternalContent;
    const hasServiceProvider = !!organisation?.serviceProvider;

    const isOfferCategoryType =
        offerEventCategoryType === EOfferEventCategoryType.Offer;

    const {
        loadingInitialValues,
        control,
        formMethods,
        isSubmitting,
        handleFormSubmit,
    } = useForm({
        edit,
        offerEvent,
        includeOfferFields,
        offerEventType,
        isOfferCategoryType,
        isOfferDetailDrawer,
        hasServiceProvider,
        currentUserIsAuthor,
        onSubmit,
    });

    const [publishDescriptionOpen, setPublishDescriptionOpen] = useState(false);
    const isNewMeetingPage = !!useRouteMatch('OFFER_MEETING_CREATE');
    const isNewOfferPage = !!useRouteMatch('OFFER_CREATE');
    const isOfferDetailPage = !!useRouteMatch('OFFER_DETAIL');
    const isOfferEventCreatePage = !!useRouteMatch('CREATE_OFFER_EVENT');
    const isOfferDetailDrawerPage =
        !!useRouteMatch('OFFER_DETAIL_DRAWER') || isOfferDetailDrawer;
    const client = useApolloClient();

    let isOnlyOfferEvent = false;

    if (isOfferDetailPage && offerEvent) {
        // Get offer events from the cache because at this point this is already cached
        const data: IOfferEventsQuery | null = client.cache.readQuery({
            query: OfferEventsDocument,
            variables: { offerId: offerEvent.offer.id },
        });

        const { offerEvents } = data || {};

        isOnlyOfferEvent = offerEvents?.length === 1;
    }

    const { canDelete = false } = useContext(OfferPermissionsContext) || {};

    const loading = loadingInitialValues || loadingOrg;

    const isWebinar = offerEventType === IOfferEventType.Webinar;
    const isMeeting = offerEventType === IOfferEventType.Meeting;
    const isWebinarOrMeeting = isWebinar || isMeeting;

    useEffect(() => {
        onLoading && onLoading(loading);
    }, [loading, onLoading]);

    const isTraining = offerEventType === IOfferEventType.Training;
    const isCourse = offerEventType === IOfferEventType.Course;

    const buttonEventText = {
        offerEventType: isTraining
            ? translate('subscription').toLowerCase()
            : getOfferEventTypeTranslation(offerEventType).toLowerCase(),
    };

    const buttonText = edit
        ? translate('editOfferEvent', buttonEventText)
        : translate('createOfferEvent', buttonEventText);

    if (!onLoading && loading) return <Loader />;
    if (onLoading && loading) return null;

    const { id: offerId, hasDetailPage } = offerEvent?.offer || {};

    let introText = translate(
        `offer.${isOfferCategoryType ? 'newOfferIntroText' : 'newMeetingIntroText'}`
    );

    if (currentUserIsAuthor && !currentUser?.isGroupManager) {
        introText = translate('offer.newOfferIntroTextAuthor');
    }

    const isNewMeetingOrOfferPage = isNewMeetingPage || isNewOfferPage;
    const canCreateOffer =
        !currentUserIsAuthor ||
        (currentUserIsAuthor && !!currentUser?.isGroupManager);

    const isELearning = offerEventType === IOfferEventType.Elearning;

    // Is of type meeting/webinar or elearning or is new offer
    const isMeetingOrELearning = isWebinarOrMeeting || isELearning;
    const showGroupFilters =
        isNewMeetingOrOfferPage || (!hasDetailPage && isOfferDetailDrawerPage);
    const showMoreSettings =
        isMeetingOrELearning || isNewOfferPage || isOfferDetailDrawerPage;

    const offerEventForm = (
        <OfferEventFormFields
            hideExternalContent={!hasExternalContent}
            isOfferDetailDrawer={isOfferDetailDrawer}
            offerEventId={offerEvent?.id}
            offerEventType={offerEventType}
        />
    );

    let title = isOfferCategoryType
        ? translate('offers')
        : getOfferEventTypeTranslation(offerEventType);

    if (isTraining) title = translate('trainingSubscription');
    if (isCourse) title = translate('courseSubscription');

    if (isNewMeetingOrOfferPage) {
        title = translate(
            isOfferCategoryType
                ? 'offer.newOfferIntroTitle'
                : 'offer.newMeetingIntroTitle'
        );
    }

    return (
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <FormProvider {...formMethods}>
                <form onSubmit={handleFormSubmit}>
                    <Box sx={{ mb: 4 }}>
                        {!!offerEvent &&
                            hasDetailPage &&
                            !isOfferDetailPage && (
                                <Link
                                    params={{ id: offerId }}
                                    sx={{
                                        fontSize: '1.6rem',
                                        textDecoration: 'underline',
                                    }}
                                    to="OFFER_DETAIL"
                                >
                                    {offerEvent.offer.title}
                                </Link>
                            )}

                        <Typography sx={{ fontWeight: 'bold' }} variant="h3">
                            {title}
                        </Typography>

                        {isNewMeetingOrOfferPage && (
                            <Box sx={{ mt: 2 }}>
                                <Typography>{introText}</Typography>
                            </Box>
                        )}
                    </Box>

                    {canCreateOffer && (
                        <>
                            {includeOfferFields && !loading && (
                                <OfferFormFields
                                    isOfferDetailDrawer={isOfferDetailDrawer}
                                    renderTypeSelect={isOfferCategoryType}
                                />
                            )}

                            {!includeOfferFields &&
                                !isNewMeetingOrOfferPage && (
                                    <Box sx={{ mb: 2 }}>
                                        <TextField
                                            control={control}
                                            label={translate('offerEventTitle')}
                                            name="offerEventTitle"
                                            sx={{ mt: 0 }}
                                        />
                                    </Box>
                                )}

                            {!isNewMeetingOrOfferPage && (
                                <TextField
                                    multiline
                                    control={control}
                                    label={translate(
                                        'offer.descriptionBeforeSubscription'
                                    )}
                                    name="descriptionBeforeSubscription"
                                    sx={[
                                        includeOfferFields
                                            ? { mt: 2 }
                                            : { mt: 0 },
                                    ]}
                                />
                            )}

                            {isOfferCategoryType && (
                                <>
                                    {!hasDetailPage &&
                                        !isOfferEventCreatePage && (
                                            <>
                                                <Box sx={{ mb: 2 }}>
                                                    <OfferTypeSelectField
                                                        control={control}
                                                        name="type"
                                                    />
                                                </Box>

                                                <Divider />
                                            </>
                                        )}

                                    <FormSection
                                        renderExpanded
                                        description={
                                            <Box>
                                                {translate(
                                                    'offer.trainingsDescription'
                                                )}
                                            </Box>
                                        }
                                        title={translate(
                                            'offer.trainingsTitle'
                                        )}
                                    >
                                        <Box sx={{ mt: 3 }}>
                                            <TrainingSelectorField
                                                control={control}
                                                name="accessTrainings"
                                            />
                                        </Box>
                                    </FormSection>

                                    {hasExternalContent && (
                                        <FormSection
                                            renderExpanded
                                            description={
                                                <Box>
                                                    {translate(
                                                        'offer.externalContentsDescription'
                                                    )}
                                                </Box>
                                            }
                                            title={translate(
                                                'offer.externalContentsTitle'
                                            )}
                                        >
                                            <Box sx={{ mt: 3 }}>
                                                <ExternalContentSelectorField
                                                    control={control}
                                                    name="accessExternalContents"
                                                />
                                            </Box>
                                        </FormSection>
                                    )}

                                    <FormSection
                                        renderExpanded
                                        description={
                                            <Box>
                                                {translate(
                                                    'offer.coursesDescription'
                                                )}
                                            </Box>
                                        }
                                        sx={{
                                            borderBottom:
                                                canUseScorm || canUseVideo
                                                    ? 1
                                                    : 0,
                                            borderBottomColor: 'divider',
                                        }}
                                        title={translate('offer.coursesTitle')}
                                    >
                                        <Box sx={{ mt: 3 }}>
                                            <CourseSelectorField
                                                control={control}
                                                name="accessCourses"
                                            />
                                        </Box>
                                    </FormSection>

                                    {!loadingCanUseScorm && canUseScorm && (
                                        <FormSection
                                            renderExpanded
                                            description={
                                                <Box>
                                                    {translate(
                                                        'offer.scormsDescription'
                                                    )}
                                                </Box>
                                            }
                                            sx={[
                                                canUseVideo
                                                    ? {
                                                          borderBottom: 1,
                                                      }
                                                    : {
                                                          borderBottom: 0,
                                                      },
                                                {
                                                    borderBottomColor:
                                                        'divider',
                                                },
                                            ]}
                                            title={translate(
                                                'offer.scormsTitle'
                                            )}
                                        >
                                            <Box sx={{ mt: 3 }}>
                                                <ScormSelectorField
                                                    control={control}
                                                    name="accessScorms"
                                                />
                                            </Box>
                                        </FormSection>
                                    )}

                                    {!loadingCanUseVideo && canUseVideo && (
                                        <FormSection
                                            renderExpanded
                                            description={
                                                <Box>
                                                    {translate(
                                                        'offer.videosDescription'
                                                    )}
                                                </Box>
                                            }
                                            title={translate(
                                                'offer.videosTitle'
                                            )}
                                        >
                                            <Box sx={{ mt: 3 }}>
                                                <VideoSelectorField
                                                    control={control}
                                                    name="accessVideos"
                                                />
                                            </Box>
                                        </FormSection>
                                    )}

                                    {!isWebinarOrMeeting && (
                                        <>
                                            <FormSection
                                                renderExpanded
                                                description={
                                                    <Box>
                                                        {translate(
                                                            'offer.offerTrainerSectionDescription'
                                                        )}
                                                    </Box>
                                                }
                                                title={translate('trainer')}
                                            >
                                                <Box sx={{ pt: 2 }}>
                                                    <FormTrainerSelectorField
                                                        control={control}
                                                        label={translate(
                                                            'addTrainer'
                                                        )}
                                                        name="trainers"
                                                        selectorQueryVariables={
                                                            isNewOfferPage ||
                                                            !offerEvent?.id
                                                                ? undefined
                                                                : {
                                                                      contentType:
                                                                          IContentTypeValue.OfferEvent,
                                                                      objectId:
                                                                          offerEvent?.id,
                                                                  }
                                                        }
                                                    />
                                                </Box>
                                            </FormSection>

                                            <FormSection
                                                renderExpanded
                                                description={
                                                    <Box>
                                                        {translate(
                                                            'offer.certificateDescription'
                                                        )}
                                                    </Box>
                                                }
                                                sx={{
                                                    borderBottom: 0,
                                                }}
                                                title={translate(
                                                    'offer.certificateTitle'
                                                )}
                                            >
                                                <CertificateSelectField
                                                    allowExternalCertificates={
                                                        isWebinarOrMeeting
                                                    }
                                                    control={control}
                                                    name="certificate"
                                                />

                                                <SliderField
                                                    control={control}
                                                    label={translate(
                                                        'offer.certificateThreshold'
                                                    )}
                                                    marks={[
                                                        {
                                                            value: 0,
                                                            label: '0%',
                                                        },
                                                        {
                                                            value: 1,
                                                            label: '100%',
                                                        },
                                                    ]}
                                                    max={1}
                                                    min={0}
                                                    name="scoreThreshold"
                                                    step={0.01}
                                                    valueLabelFormat={(
                                                        value: number
                                                    ) =>
                                                        `${Math.floor(value * 100)}%`
                                                    }
                                                />
                                            </FormSection>
                                        </>
                                    )}
                                </>
                            )}

                            {!isOfferCategoryType && (
                                <MeetingFormFields
                                    hasServiceProvider={hasServiceProvider}
                                    offerEventId={offerEvent?.id}
                                />
                            )}

                            {showGroupFilters && (
                                <GroupConditionsSection
                                    control={control}
                                    currentUserIsAuthor={currentUserIsAuthor}
                                    isWebinarOrMeeting={isWebinarOrMeeting}
                                />
                            )}

                            <SubscribeSettingsSection
                                isMeeting={isMeeting}
                                isWebinarOrMeeting={isWebinarOrMeeting}
                                showGroupFilters={showGroupFilters}
                                showMoreSettings={showMoreSettings}
                            />

                            {showMoreSettings && isWebinarOrMeeting && (
                                <MoreSettingsExpander
                                    offerEventType={offerEventType}
                                >
                                    {offerEventForm}
                                </MoreSettingsExpander>
                            )}

                            {showMoreSettings &&
                                !isWebinarOrMeeting &&
                                offerEventForm}

                            <Box sx={{ mt: 3 }}>
                                <Box sx={{ mb: 3 }}>
                                    <Grid
                                        container
                                        sx={{
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Grid
                                            sx={{
                                                mr: -1,

                                                '> .MuiFormControl-root': {
                                                    m: 0,
                                                },
                                            }}
                                        >
                                            <SwitchField
                                                control={control}
                                                label={translate('published')}
                                                name="publish"
                                            />
                                        </Grid>

                                        <Grid>
                                            <DescriptionButton
                                                onClick={() =>
                                                    setPublishDescriptionOpen(
                                                        !publishDescriptionOpen
                                                    )
                                                }
                                            />
                                        </Grid>
                                    </Grid>

                                    <DescriptionBox
                                        open={publishDescriptionOpen}
                                        onClose={() =>
                                            setPublishDescriptionOpen(false)
                                        }
                                    >
                                        {translate(
                                            'offer.offerPublishInfoDescription'
                                        )}
                                    </DescriptionBox>
                                </Box>

                                <Divider />
                            </Box>

                            <FormFooter
                                canDelete={
                                    canDelete && !!onDelete && !isOnlyOfferEvent
                                }
                                deleteDialogText={translate(
                                    isOfferDetailDrawer
                                        ? 'deleteOfferMessage.text'
                                        : 'deleteOfferEvent.text'
                                )}
                                deleteDialogTitle={translate(
                                    isOfferDetailDrawer
                                        ? 'deleteOfferMessage.title'
                                        : 'deleteOfferEvent.title'
                                )}
                                disabled={disabled}
                                submitButtonText={buttonText}
                                onClose={onClose}
                                onDelete={onDelete}
                            >
                                {hasServiceProvider && (
                                    <ShowAfterDelay
                                        delay={2000}
                                        show={isSubmitting}
                                    >
                                        <Box
                                            sx={{
                                                ml: 2,
                                                lineHeight: 1.1,
                                                fontSize: 14,
                                            }}
                                        >
                                            {translate(
                                                'syncingToServiceProviderMicrosoftEntra'
                                            )}
                                        </Box>
                                    </ShowAfterDelay>
                                )}
                            </FormFooter>
                        </>
                    )}
                </form>
            </FormProvider>
        </Box>
    );
};
